import React, { useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Container, Paragraph } from '../../elements';
import GhostButton from '../Shared/GhostButton';
import handleFormSubmit from '../../actions/handleFormSubmit';
import { syncCheckedEndorsements } from '../../actions/application';
import { getAvailableEndorsements } from '../../selectors/availableEndorsements';
import { selectEndorsements, selectLimitDamagePremRented } from '../../selectors/application';
import { endorsementRegistry } from './endorsementRegistry';
import { EndorsementKey } from './types';
import EquipmentBreakdown from './components/EquipmentBreakdown/EquipmentBreakdown.addon';
import RestaurantCoverage from './components/RestaurantCoverage/RestaurantCoverage.addon';
import CyberInsurance from './components/CyberInsurance/CyberInsurance.addon';
import WorkplaceViolence from './components/WorkplaceViolence/WorkplaceViolence.addon';
import EPLI from './components/EPLI/EPLI.addon';
import HiredAuto from './components/HiredAuto/HiredAuto.addon';
import LiquorLiability from './components/LiquorLiability/LiquorLiability.addon';
import ContractorTools from './components/ContractorTools/ContractorTools.addon';
import DamagesToPremisesRented from './components/DamagesToPremisesRented/DamagesToPremisesRented.addon';
import sparkles from '../../icons/sparkles.svg';
import shieldCheck from '../../icons/shield-check.svg';

const endorsementComponents: Record<string, React.ComponentType> = {
  [EndorsementKey.EquipmentBreakdownEndorsement]: EquipmentBreakdown,
  [EndorsementKey.RestaurantCoverage]: RestaurantCoverage,
  [EndorsementKey.CyberCoverage]: CyberInsurance,
  [EndorsementKey.WorkplaceViolenceCoverage]: WorkplaceViolence,
  [EndorsementKey.EpliCoverage]: EPLI,
  [EndorsementKey.HiredAutoAndNonOwnedAuto]: HiredAuto,
  [EndorsementKey.LiquorLiabilityEndorsement]: LiquorLiability,
  [EndorsementKey.ContractorsInstallationAndTools]: ContractorTools,
  [EndorsementKey.LimitDamagePremRented]: DamagesToPremisesRented,
};

const pageDescription =
  'Based on your choices, we think you should consider ' +
  'the following additional coverages. ' +
  'Select the ones you want to add to your policy.';

interface EndorsementsPageProps {
  handleFormSubmit: () => void;
  availableEndorsements: AvailableEndorsementsState;
}

const EndorsementsPage = ({ handleFormSubmit, availableEndorsements }: EndorsementsPageProps) => {
  const dispatch = useDispatch();
  const endorsementsState = useSelector(selectEndorsements);
  const limitDamagePremRented = useSelector(selectLimitDamagePremRented);

  useEffect(() => {
    const checked: EndorsementKey[] = [];
    if (endorsementsState) {
      Object.keys(endorsementsState).forEach((k) => {
        if (endorsementsState[k] != null) checked.push(k as EndorsementKey);
      });
    }
    if (limitDamagePremRented != null) checked.push(EndorsementKey.LimitDamagePremRented);
    dispatch(syncCheckedEndorsements(checked));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleFormSubmit();
  };

  const endorsementKeys = Object.keys({
    ...availableEndorsements,
    [EndorsementKey.LimitDamagePremRented]: undefined,
  });

  const recommendedKeys = endorsementKeys.filter(
    (key) => endorsementRegistry[key] && endorsementRegistry[key].recommended
  );

  const otherKeys = endorsementKeys.filter((key) => endorsementRegistry[key] && !endorsementRegistry[key].recommended);

  return (
    <Container data-cy="Add-Ons-Page">
      <Paragraph>{pageDescription}</Paragraph>
      <Form onSubmit={handleSubmit}>
        <EndorsementHeader>
          <EndorsementHeaderImage src={sparkles} /> Recommended
        </EndorsementHeader>
        {recommendedKeys.map((key) => {
          const Component = endorsementComponents[key];
          if (!Component) return null;
          return <Component key={key} />;
        })}
        <EndorsementHeader>
          <EndorsementHeaderImage src={shieldCheck} />
          Additional Endorsements
        </EndorsementHeader>
        {otherKeys.map((key) => {
          const Component = endorsementComponents[key];
          if (!Component) return null;
          return <Component key={key} />;
        })}
        <GhostButton />
      </Form>
    </Container>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  availableEndorsements: getAvailableEndorsements(state),
});

const EndorsementHeader = styled.h2`
  text-transform: uppercase;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  margin-bottom: 16px;
  margin-top: 0px;
  display: flex;
  align-items: center;

  &:last-of-type {
    margin-top: 56px;
  }
`;

const Form = styled.form`
  margin-top: 32px;
`;

const EndorsementHeaderImage = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 16px;
`;

export default connect(mapStateToProps, { handleFormSubmit })(EndorsementsPage);
